/**
 * Handles sync-related events for the notification service
 * @param {Object} service - The notification service instance
 * @returns {Object} Methods for handling sync events
 */

import { syncConfig } from '../../config';

export const createSyncHandlers = (service) => {
  /**
   * Handle sync started event
   * @param {CustomEvent} event - The sync started event
   */
  const handleSyncStarted = (event) => {
    // console.log('[NotificationService] Sync started, pausing notification checking', event.detail);
    // Pause notification checking during sync
    service.pauseNotificationChecking();
  };

  /**
   * Handle sync completed event
   * @param {CustomEvent} event - The sync completed event
   */
  const handleSyncCompleted = (event) => {
    // console.log('[NotificationService] Sync completed, will resume notification checking', event.detail);
    // Resume notification checking after sync with a delay
    // This ensures all data is properly stored before we check
    setTimeout(() => {
      service.resumeNotificationChecking();
    }, 1000);
  };

  /**
   * Handle sync failed event
   * @param {CustomEvent} event - The sync failed event
   */
  const handleSyncFailed = (event) => {
    // console.log('[NotificationService] Sync failed, resuming notification checking', event.detail);
    // Resume notification checking even if sync failed
    service.resumeNotificationChecking();
  };

  /**
   * Handle notifications synced event
   * @param {CustomEvent} event - The notifications synced event
   */
  const handleNotificationsSynced = (event) => {
    // console.log('[NotificationService] Notifications synced from server, refreshing registry', event.detail);
    // Refresh the notification registry to include synced notifications
    if (service.currentUserId) {
      service.notificationRegistry.initialize(service.currentUserId);
      //  .then(() => console.log('[NotificationService] Registry refreshed after notifications sync'));
    }
  };

  /**
   * Set up event listeners for sync events
   */
  const setupSyncEventListeners = () => {
    if (syncConfig.enableSync) {
      window.addEventListener('sync:sync_started', handleSyncStarted);
      window.addEventListener('sync:sync_completed', handleSyncCompleted);
      window.addEventListener('sync:sync_failed', handleSyncFailed);
      window.addEventListener('sync:notifications_synced', handleNotificationsSynced);
      // console.log('[NotificationService] Sync event listeners registered');
    }
  };

  /**
   * Remove sync event listeners
   */
  const removeSyncEventListeners = () => {
    if (syncConfig.enableSync) {
      window.removeEventListener('sync:sync_started', handleSyncStarted);
      window.removeEventListener('sync:sync_completed', handleSyncCompleted);
      window.removeEventListener('sync:sync_failed', handleSyncFailed);
      window.removeEventListener('sync:notifications_synced', handleNotificationsSynced);
    }
  };

  return {
    handleSyncStarted,
    handleSyncCompleted,
    handleSyncFailed,
    handleNotificationsSynced,
    setupSyncEventListeners,
    removeSyncEventListeners
  };
}; 